import React from "react";
import {
  AdvancedSliceBase,
  AdvancedSliceContainer,
  AdvancedSliceContent,
  TextContainer,
  ImageContainer,
  Image,
  Description,
  RepeatableItems,
  RepeatableItem,
  RepeatableItemImage,
  RepeatableItemText,
  CTAStyled,
} from "./advanced-slice.styled";
import {
  AdvancedSliceSliceFragment,
  ContentPostFragment,
} from "../../types/graphql";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import { htmlSerializer } from "../../utils/htmlSerializer";
import { Slice } from "@prismicio/types";
import { IPageContext } from "../../types/types";
import { SharedSliceType } from "../shared-slice/shared-slice";
import { GatsbyImage } from "gatsby-plugin-image";
import SmallPostCard from "../small-post-card/small-post-card";

export type IAdvancedSliceSliceProps = Slice<
  SharedSliceType,
  AdvancedSliceSliceFragment["primary"],
  AdvancedSliceSliceFragment["items"][number]
> & { type: SharedSliceType.advanced_slice };

const AdvancedSlice = ({
  slice,
}: SliceComponentProps<IAdvancedSliceSliceProps, IPageContext>) => {
  const advancedSlice = slice.primary;
  const imageAsBackground = advancedSlice.use_image_as_background
    ? "image-background"
    : "";
  const textOnly = !advancedSlice.image?.gatsbyImageData ? "text-only" : "";
  const justifyImage =
    advancedSlice.text_and_image_alignment === "right-align-text" &&
    advancedSlice.image_width === "auto"
      ? "justify-self-end"
      : "";

  const RepeatableItemIcon = (
    props: AdvancedSliceSliceFragment["items"][number]
  ) => {
    const { image, cta_text, description } = props;
    return (
      <>
        {image?.gatsbyImageData && (
          <RepeatableItemImage className="repeatable-item-image">
            <GatsbyImage
              image={image?.gatsbyImageData}
              alt={image?.alt || "Xometry Resources"}
              loading="lazy"
            />
          </RepeatableItemImage>
        )}
        <RepeatableItemText className="repeatable-item-text">
          {cta_text ? (
            <>{cta_text}</>
          ) : description ? (
            <PrismicRichText
              field={description.richText}
              components={htmlSerializer}
            />
          ) : (
            <></>
          )}
        </RepeatableItemText>
      </>
    );
  };

  const RepeatableItemContent = (
    props: AdvancedSliceSliceFragment["items"][number]
  ) => {
    const { heading, description, image, cta_text, cta_link } = props;
    return (
      <>
        {image?.gatsbyImageData && (
          <RepeatableItemImage className="repeatable-item-image">
            <GatsbyImage
              image={image?.gatsbyImageData}
              alt={image?.alt || "Xometry Resources"}
              loading="lazy"
            />
          </RepeatableItemImage>
        )}
        <RepeatableItemText className="repeatable-item-text">
          {heading && (
            <PrismicRichText
              field={heading.richText}
              components={htmlSerializer}
            />
          )}
          {description && (
            <PrismicRichText
              field={description.richText}
              components={htmlSerializer}
            />
          )}
          {cta_text && cta_link && (
            <CTAStyled
              kind="primary"
              pageSection="advanced-slice"
              ctaText={cta_text}
              linkURL={cta_link && cta_link?.url}
              target={cta_link && cta_link?.target}
            />
          )}
        </RepeatableItemText>
      </>
    );
  };

  return (
    (slice && (
      <AdvancedSliceBase
        className={`slice-section ${advancedSlice.padding} ${advancedSlice.theme} ${imageAsBackground} ${advancedSlice.layout}`}
      >
        <div className="container">
          <AdvancedSliceContainer
            className={`${advancedSlice.layout} ${advancedSlice.container_width}`}
          >
            <AdvancedSliceContent
              className={`content ${advancedSlice.text_and_image_alignment} ${imageAsBackground} ${textOnly} ${advancedSlice.layout}`}
            >
              {(advancedSlice.primary_heading.richText.length > 0 ||
                advancedSlice.primary_description.richText.length > 0) && (
                <TextContainer
                  className={`text-container ${advancedSlice.text_and_image_alignment} ${imageAsBackground} ${textOnly} ${advancedSlice.layout}`}
                >
                  {advancedSlice.primary_heading.richText.length > 0 && (
                    <header>
                      <PrismicRichText
                        field={advancedSlice.primary_heading.richText}
                        components={htmlSerializer}
                      />
                    </header>
                  )}
                  {advancedSlice.primary_description.richText.length > 0 && (
                    <Description styleType={advancedSlice.description_styling}>
                      <PrismicRichText
                        field={advancedSlice.primary_description.richText}
                        components={htmlSerializer}
                      />
                    </Description>
                  )}
                  {advancedSlice?.cta_text && advancedSlice.cta_link?.url && (
                    <CTAStyled
                      kind="primary"
                      pageSection="advanced-slice"
                      ctaText={advancedSlice?.cta_text}
                      linkURL={
                        advancedSlice?.cta_link && advancedSlice?.cta_link?.url
                      }
                      target={
                        advancedSlice?.cta_link &&
                        advancedSlice?.cta_link?.target
                      }
                    />
                  )}
                </TextContainer>
              )}
              {advancedSlice.image?.gatsbyImageData && (
                <ImageContainer
                  className={`${imageAsBackground} ${justifyImage} ${advancedSlice.layout}`}
                >
                  {advancedSlice.image?.gatsbyImageData && (
                    <Image
                      image={advancedSlice.image?.gatsbyImageData}
                      alt={advancedSlice.image?.alt || "Xometry Resources"}
                      className={`${imageAsBackground} ${slice.primary.image_background_alignment} ${advancedSlice.image_width} ${advancedSlice.layout}`}
                      loading="lazy"
                    />
                  )}
                </ImageContainer>
              )}
            </AdvancedSliceContent>
            {slice.items.length > 0 && (
              <RepeatableItems
                className={`items ${advancedSlice.repeatable_layout}`}
              >
                {slice.items?.map((repeatable, index) => {
                  return (
                    (repeatable &&
                      (repeatable.content_post ? (
                        <SmallPostCard
                          key={index}
                          post={
                            repeatable.content_post
                              .document as ContentPostFragment
                          }
                        />
                      ) : (
                        <RepeatableItem
                          className={`${advancedSlice.repeatable_layout}`}
                        >
                          {repeatable.cta_link &&
                          repeatable.cta_link.url &&
                          advancedSlice.repeatable_layout ===
                            "four-column-grid-icons" ? (
                            <a
                              href={repeatable.cta_link.url}
                              className={advancedSlice.repeatable_layout}
                            >
                              <RepeatableItemIcon {...repeatable} />
                            </a>
                          ) : advancedSlice.repeatable_layout ===
                            "four-column-grid-icons" ? (
                            <span>
                              <RepeatableItemIcon {...repeatable} />
                            </span>
                          ) : (
                            <RepeatableItemContent {...repeatable} />
                          )}
                        </RepeatableItem>
                      ))) || <></>
                  );
                })}
              </RepeatableItems>
            )}
          </AdvancedSliceContainer>
        </div>
      </AdvancedSliceBase>
    )) || <></>
  );
};

export default AdvancedSlice;
